import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { CapabilityTier } from '@signalvote/shared';
import type { QuestionAnswersFilter } from '@signalvote/shared';
import { QuestionAnswersPage } from '@/components/analytics';
import { useCampaignStore } from '@/stores';
import { requireCapability } from '@/lib/routeGuards';

const sortBySchema = z.enum(['contactTimestamp', 'voterLastName', 'answer']);
const sortOrderSchema = z.enum(['asc', 'desc']);

const questionAnswersSearchSchema = z.object({
  answer: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
  sortBy: sortBySchema.optional(),
  sortOrder: sortOrderSchema.optional(),
  campaignId: z.string().optional(),
});

export const Route = createFileRoute('/app/analytics/questions/$questionId/answers')({
  validateSearch: questionAnswersSearchSchema,
  beforeLoad: ({ context }) => {
    requireCapability(context, 'Analytics', CapabilityTier.View, '/app/home');
  },
  component: QuestionAnswersRoute,
});

function QuestionAnswersRoute() {
  const { questionId } = Route.useParams();
  const filter: QuestionAnswersFilter = Route.useSearch();
  const navigate = useNavigate();
  const { selectedCampaign, canManage } = useCampaignStore();

  const handleFilterChange = (next: QuestionAnswersFilter) => {
    navigate({
      to: '/app/analytics/questions/$questionId/answers',
      params: { questionId },
      search: next,
    });
  };

  return (
    <QuestionAnswersPage
      questionId={questionId}
      campaignId={selectedCampaign?.id ?? ''}
      filter={filter}
      canManageAnalytics={canManage('Analytics')}
      onFilterChange={handleFilterChange}
    />
  );
}
